import { CustomWidget, WidgetKeys } from '../../providers/model/custom-widget';
import LatestRealizedVolsWidget from '../v2/widgets/latest-realized-vols';
import VolatilityRangeWidget from '../v2/widgets/volatility-range';
import TermStructureWidget from '../v2/widgets/term-structure';
import ScatterPlotWidget from '../v2/widgets/scatter-plot';
import FootprintVolatilityCurvesWidget from '../v2/widgets/footprint-volatility-curves';
import VolatilityConesWidget from '../v2/widgets/volatility-cones';
import VolatilityHeatmapWidget from '../v2/widgets/volatility-heatmap';
import DefaultWidget from '../../widgets/default-widget/default-widget';
import ModelVolatilitySurfaceWidget from '../v2/widgets/model-volatility-surface';
import VolatilitySmileWidget from '../v2/widgets/volatility-smile';
import VolatilitySmileComparisonWidget from '../v2/widgets/volatility-smile-comparison';
import TermStructureComparisonWidget from '../v2/widgets/term-structure-comparison';
import VolatilitySurfaceWidget from '../v2/widgets/volatility-surface';
import VolatilitySurfaceMarkingWidget from '../v2/widgets/volatility-surface-marking';
import { MarketHistoryWrapperProvider as MarketHistoryWidget } from '../v2/widgets/market-history';
import { WidgetProvider } from '../v2/widget-renderer/widget-provider';
import OpenInterestWidget from '../v2/widgets/open-interest';
import RealizedAndImpliedVolsWidget from '../v2/widgets/realized-and-implied-vols';
import ImpliedRealizedVolatilityWidget from '../v2/widgets/implied-realized-volatility';
import VolSurfaceTimeseriesWidget from '../v2/widgets/vol-surface-timeseries';

interface IRenderWidget {
  widget: CustomWidget;
  handleSettingsWidget?: (cardKey: string) => void;
  handleDeleteWidget?: (cardKey: string) => void;
  maximizeCard: (card: string) => void;
  maximized: string;
  showFooter?: boolean;
  classNames?: string;
  isCustomTab?: boolean;
  providerKey?: string;
  customWidgetKey?: string;
}

const RenderWidget = (props: IRenderWidget) => {
  const {
    widget,
    maximizeCard,
    handleSettingsWidget,
    handleDeleteWidget,
    showFooter,
    classNames = '',
  } = props;

  let providerKey = props.providerKey || widget.id || widget.key;

  if (props.customWidgetKey) {
    providerKey = `${providerKey}${props.customWidgetKey}`;
  }

  const handleCloseWidget = () => {
    if (handleDeleteWidget && widget.id) {
      handleDeleteWidget(widget.id);
    }
  };

  const handleDuplicateWidget = () => {
    if (handleSettingsWidget && widget.key) {
      handleSettingsWidget(widget.key);
    }
  };

  const handleMaximizeWidget = () => {
    if (maximizeCard && widget.id) {
      maximizeCard(widget.id);
    }
  };

  const widgetActions = {
    handleMaximizeWidget,
    handleDuplicateWidget,
    handleCloseWidget,
  };

  const renderNewWidget = () => {
    const newWidgets: Record<string, React.FC<any>> = {
      [WidgetKeys.VolatilityCones]: VolatilityConesWidget,
      [WidgetKeys.LatestRealizedVols]: LatestRealizedVolsWidget,
      [WidgetKeys.ModelVolatilitySurface]: ModelVolatilitySurfaceWidget,
      [WidgetKeys.VolatilitySurfaceMarking]: VolatilitySurfaceMarkingWidget,
      [WidgetKeys.VolatilitySmile]: VolatilitySmileWidget,
      [WidgetKeys.VolSmileComparison]: VolatilitySmileComparisonWidget,
      [WidgetKeys.TermStructure]: TermStructureWidget,
      [WidgetKeys.TermStructureComparison]: TermStructureComparisonWidget,
      [WidgetKeys.VolatilityRange]: VolatilityRangeWidget,
      [WidgetKeys.VolatilityHeatmap]: VolatilityHeatmapWidget,
      [WidgetKeys.ScatterPlot]: ScatterPlotWidget,
      [WidgetKeys.FootprintVolatility]: FootprintVolatilityCurvesWidget,
      [WidgetKeys.OpenInterest]: OpenInterestWidget,

      [WidgetKeys.RealizedAndImpliedVols]: RealizedAndImpliedVolsWidget,
      [WidgetKeys.TokenVolatilityTimeseries]: RealizedAndImpliedVolsWidget, //old key
      [WidgetKeys.ImpliedRealizedVolatility]: ImpliedRealizedVolatilityWidget,
      [WidgetKeys.VolSurfaceTimeseries]: VolSurfaceTimeseriesWidget,
    };

    const WidgetComponent = newWidgets[widget.key];

    if (WidgetComponent) {
      return (
        <WidgetProvider providerKey={providerKey}>
          <WidgetComponent providerKey={providerKey} actions={widgetActions} widget={widget} />
        </WidgetProvider>
      );
    }

    return null;
  };

  const newWidget = renderNewWidget();
  if (newWidget) {
    return newWidget;
  }

  if (widget.key === WidgetKeys.LatestRealizedVols || widget.key === WidgetKeys.TokenData) {
    return (
      <LatestRealizedVolsWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget,
          handleDuplicateWidget,
          handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }
  if (widget.key === WidgetKeys.ModelVolatilitySurface) {
    return (
      <ModelVolatilitySurfaceWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget,
          handleDuplicateWidget,
          handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }
  if (widget.key === WidgetKeys.PerpsTimeseries) {
    return (
      <WidgetProvider providerKey={providerKey}>
        <VolSurfaceTimeseriesWidget
          providerKey={providerKey}
          actions={{
            handleMaximizeWidget,
            handleDuplicateWidget,
            handleCloseWidget,
          }}
          widget={widget}
          onlyPerps
        />
      </WidgetProvider>
    );
  }

  if (widget.key === WidgetKeys.VolatilitySmile || widget.key === WidgetKeys.VolSmile) {
    return (
      <VolatilitySmileWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.VolSmileComparison) {
    return (
      <VolatilitySmileComparisonWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.TermStructure) {
    return (
      <TermStructureWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.TermStructureComparison) {
    return (
      <TermStructureComparisonWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.MarketHistory) {
    return (
      <MarketHistoryWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.VolSurface) {
    return (
      <VolatilitySurfaceWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
        isCustomWidget
      />
    );
  }

  if (widget.key === WidgetKeys.VolatilityRange) {
    return (
      <VolatilityRangeWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget,
          handleDuplicateWidget,
          handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.VolatilityHeatmap) {
    return (
      <VolatilityHeatmapWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget: handleMaximizeWidget,
          handleDuplicateWidget: handleDuplicateWidget,
          handleCloseWidget: handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.ScatterPlot) {
    return (
      <ScatterPlotWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget,
          handleDuplicateWidget,
          handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  if (widget.key === WidgetKeys.FootprintVolatility) {
    return (
      <FootprintVolatilityCurvesWidget
        providerKey={providerKey}
        actions={{
          handleMaximizeWidget,
          handleDuplicateWidget,
          handleCloseWidget,
        }}
        widget={widget}
      />
    );
  }

  return (
    <DefaultWidget
      title={widget.title}
      cardId={widget.id}
      cardKey={widget.key}
      showFooter={widget.noFooter ? false : showFooter}
      widget={null}
      handleDeleteWidget={props.handleDeleteWidget}
      handleSettingsWidget={props.handleSettingsWidget}
      maximizeCard={props.maximizeCard}
      maximized={props.maximized}
      classNames={classNames}
    />
  );
};

export default RenderWidget;
